import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  Observable,
  Subscription,
  take,
} from 'rxjs';
import { selectIsFullyAuthenticated } from 'src/app/store/app-ready.selectors';
import { readStorage, removeStorage, writeStorage } from 'src/app/shared/utils/safe-storage';
import { AccessTokenHolder } from 'src/app/auth/services/access-token-holder.service';
import { AuthToken } from 'src/app/auth/models/auth';
import { isPushSupported, PushStatus } from './push-status';
import { getPushDeviceId, getPushDeviceSecret, setPushDeviceSecret } from './push-device-id';
import { PushApiService, PushSubscriptionBody } from './push-api.service';

export const SW_URL = '/sw-push.js';

/**
 * The HTTP statuses that mean "this server does not have the push route", as opposed to "the call
 * did not get through". 404 is a backend built without the push endpoints; 501 is one that routes
 * them and has the feature switched off. Both are a settled ANSWER about the server, so the
 * capability check records them instead of leaving the question open — see {@link
 * PushNotificationService.checkCapability} for what leaving it open costs.
 *
 * Deliberately NOT including 0 (network/CORS failure), 502/503/504 (a gateway or a restart) or 401
 * (a bearer problem, not a route problem): every one of those is a server that may well have push
 * and simply could not say so, and declaring the feature dead on a blip hides an icon that works.
 */
const ROUTE_ABSENT_STATUSES = [404, 501];

/**
 * How much of `navigator.userAgent` is sent as a device's `label`. Shared by BOTH writers of that
 * column -- the registration POST and the permission report.
 *
 * ONE CONSTANT, deliberately. The two truncated at 120 and 160 while writing the same row, so a
 * device's displayed name in the admin panel changed length depending on which call wrote it last:
 * the same machine read as two different strings on consecutive reads, on the one panel whose job
 * is to tell devices apart.
 *
 * 120 rather than the 160 the column allows, because it is the length BOTH endpoints have always
 * accepted -- widening to 160 on the strength of one endpoint's @Size would risk a 400 on the
 * other, and a label that 400s on the report means no refusal is ever recorded, i.e. the whole
 * feature looking like it works. A modern userAgent is routinely longer than either.
 */
const PUSH_LABEL_MAX_CHARS = 120;

/**
 * This browser, named for the admin panel. Never throws: `userAgent` is absent in some non-browser
 * shims, and this is reached from teardown paths that must not fail.
 */
function deviceLabel(): string {
  return (navigator.userAgent ?? '').slice(0, PUSH_LABEL_MAX_CHARS);
}

/**
 * How long a fetched VAPID key is reused instead of re-fetched.
 *
 * DELIBERATELY SHORT, and this is a burst window rather than a cache. Fetching the key rather than
 * bundling it is what makes a server-side rotation self-healing: every subscribe attempt compares
 * the live key against the one the browser's subscription was minted with, and a mismatch drops and
 * re-mints. Holding the key for the life of the page would save one GET per token rotation and cost
 * that property -- a rotation would go unnoticed until the tab was reloaded, with the icon reading
 * `active` on a device the push service has stopped accepting sends for.
 *
 * What this DOES collapse is the burst: `onAuthenticated()` runs `checkCapability()` and then
 * `subscribeAndPost()` within the same tick, so a login fetched the identical key twice. Anything
 * further apart than this window still pays for a fresh read, which is the point.
 */
const VAPID_KEY_FRESH_MS = 10000;


/**
 * The endpoint this device last successfully POSTed to the backend.
 *
 * This is what makes `active` mean "THE BACKEND KNOWS THIS ENDPOINT" rather than merely "the
 * browser holds a subscription". Those two came apart in three ways, all of them silent and all of
 * them leaving the icon reading healthy on a device receiving nothing:
 *
 *  - logout DELETEd the backend row while the browser subscription lived on;
 *  - `pushsubscriptionchange` rotated the browser's subscription behind the app's back;
 *  - a VAPID key rotation invalidated the subscription the browser still reports as live.
 *
 * Comparing the live endpoint against this one turns all three into `inactive`, which the
 * authenticated-transition re-POST then repairs. A refused write only costs a redundant re-POST
 * (`register` is an idempotent upsert on the endpoint), so it fails safe.
 */
export const PUSH_POSTED_ENDPOINT_STORAGE_KEY = 'push.postedEndpoint';

/**
 * Attempts `subscribeAndPost()` gets on the identity-switch path, where a single swallowed failure
 * is not a cosmetic loss: the backend row still maps this device's endpoint to the OUTGOING user,
 * so their notifications keep landing on the incoming user's lock screen carrying the outgoing
 * user's record identity. Everywhere else one attempt is enough — the next authenticated
 * transition retries anyway.
 */
const IDENTITY_SWITCH_ATTEMPTS = 3;

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private statusSubject = new BehaviorSubject<PushStatus>('off');
  readonly status$: Observable<PushStatus> = this.statusSubject.asObservable();

  /**
   * Whether THIS device is marked personal or shared. Learned from the POST response — see
   * {@link trySubscribeAndPost} — never from local state, so it can't drift from what the backend
   * actually has stored. Defaults `true`: an unmarked machine is treated as one anyone can walk up
   * to, the safe direction to fail (see the phase-2 design doc, "Shared is the default").
   */
  private sharedSubject = new BehaviorSubject<boolean>(true);
  readonly shared$: Observable<boolean> = this.sharedSubject.asObservable();

  private registration?: ServiceWorkerRegistration;
  /** Last known endpoint, kept so teardown can DELETE the right row without awaiting. */
  private currentEndpoint: string | null = null;

  /**
   * In-memory mirror of the persisted posted-endpoint, seeded once at construction.
   *
   * It is the source of truth for the life of the page: a refused `setItem` writes NOTHING, and
   * `safe-storage` never throws, so a field initializer is a safe place to read from.
   */
  private postedEndpoint = readStorage('local', PUSH_POSTED_ENDPOINT_STORAGE_KEY);

  /** Guards against a second init() wiring a second auth subscription. */
  private initialized = false;

  /**
   * Monotonic counter bumped by {@link removeCurrentSubscription}. Every in-flight
   * {@link trySubscribeAndPost} captures it on entry and re-reads it at each await boundary; a
   * mismatch means the session was torn down underneath it and the attempt is ABANDONED.
   *
   * Without it a heartbeat that was already in flight when teardown ran would re-register the
   * device for a user who has logged out — and, because `subscriptionFor` mints a fresh
   * subscription when it finds none, the very local unsubscribe teardown had just done is what
   * makes it mint one. The window is real on the ordinary refresh path: a 401 refreshes, the
   * refreshed token fires `token$` (heartbeat starts), the retried request 401s again and
   * `endSession()` tears the session down while the heartbeat is still awaiting the POST. Phase 1
   * only had this window at login; the heartbeat opens it at every token rotation.
   */
  private epoch = 0;

  /**
   * The subscribe/POST attempt currently running, shared by every non-retrying caller. See
   * {@link subscribeAndPost} for why concurrent attempts produced duplicate backend rows.
   */
  private inFlightSubscribe: Promise<PushStatus> | null = null;

  /**
   * The permission state last POSTed, so a status read does not report the same answer repeatedly.
   *
   * CLEARED BY {@link removeCurrentSubscription}, and that is not tidiness. The dedup is against
   * what the BACKEND was told, and the backend keys the record to the CALLING user -- so it is a
   * per-session fact, not a per-browser one. On a shared terminal (the machine this phase exists
   * for) user A's refusal was reported, A signs out without a reload, B signs in: a stale `denied`
   * here matched B's read, nothing was POSTed, and B's refusal was invisible to the admin for the
   * life of the tab.
   */
  private lastReportedPermission: NotificationPermission | null = null;

  /**
   * Whether the delivery half of this device's row has already been dropped with nothing
   * registered since -- i.e. whether a further DELETE would be a request that changes nothing.
   *
   * The user-initiated logout path calls {@link removeCurrentSubscription} twice on purpose (once
   * before the POST, once from `clearSession()`'s own teardown), and `logoutEverywhere` bumps
   * `tokens_valid_from` in between, so the second DELETE is not merely redundant: it is a
   * guaranteed 401, silently swallowed by the interceptor carve-out. Reset on every successful
   * registration, and on a FAILED removal, so a dropped DELETE is still retried by the next call
   * rather than skipped on the strength of an attempt that never landed.
   */
  private deliveryRemoved = false;

  /**
   * Whether a removal was ATTEMPTED and never landed, so the backend may still be delivering to a
   * session that has ended.
   *
   * {@link removeCurrentSubscription} clears `currentEndpoint` and `postedEndpoint` BEFORE it fires
   * the DELETE, which is right -- after this call `active` is no longer true whatever the request
   * does -- but it left the retry with nothing to work from: {@link dropDeliveryAfterPermissionLoss}
   * returns early precisely when both are empty, so a failed DELETE reset `deliveryRemoved` to
   * `false` and then nothing ever called again.
   *
   * The case that produces it is a COLD BOOT with no valid refresh cookie: `init()` reads a revoked
   * permission beside a `push.postedEndpoint` an earlier session stored, the DELETE goes out with no
   * bearer, 401s, and the interceptor carve-out swallows it in silence. Without this flag the row
   * kept its delivery half until the push service happened to answer 410. With it, the next status
   * read that has a session behind it retries.
   */
  private deliveryRemovalOwed = false;

  /**
   * Whether this server has push switched on at all (a non-empty VAPID key). `true` until proven
   * otherwise: nothing has asked yet, and a server that HAS push on must not be misread as
   * unavailable before the first check runs.
   *
   * Checked at the authenticated transition (see {@link checkCapability}), independent of
   * `Notification.permission` — a server with push off is unavailable to a device that has never
   * been asked, one that was denied, and one that is already granted, alike.
   */
  private capable = true;

  /**
   * Whether {@link capable} holds an ANSWER rather than its optimistic default. `capable` starts
   * `true` meaning "nobody has asked yet", which is the right default everywhere except the one
   * place a wrong reading is unrecoverable -- immediately before a permission prompt. See
   * {@link enable}, which reads the cached answer synchronously when there is one and pays for a
   * fresh check only when there is not.
   */
  private capabilityResolved = false;

  /**
   * Pause between the identity-switch retries. A field rather than a constant purely so a spec can
   * drive the retry path without spending real seconds on it.
   */
  private retryDelayMs = 2000;

  /**
   * The last VAPID key read off the server, with the moment it was read. See
   * {@link VAPID_KEY_FRESH_MS} for why this is a burst window and deliberately not a page cache.
   */
  private vapidKeyCache: { key: string; at: number } | null = null;

  /**
   * The one-shot armed by {@link refreshOnNextBearer}, kept so a second identity switch arriving
   * before the first bearer does not leave two waiting on the same token.
   */
  private pendingBearerRefresh: Subscription | null = null;

  constructor(
    private api: PushApiService,
    private store: Store,
    private tokenHolder: AccessTokenHolder,
    private zone: NgZone,
  ) {}

  /**
   * Called once from AppComponent. Publishes the current state and wires the per-session re-POST.
   *
   * DOES NOT PROMPT, and never has: `Notification.requestPermission()` has exactly one reachable
   * call site in the app, `enable()`, reached from the modal's Enable button.
   *
   * Registration of the worker is LAZY — it happens on the first call that actually needs a
   * registration (`existingSubscription()` / `subscribeAndPost()`), not here.
   *
   * Nothing is POSTed from this method. The subscription is (re-)POSTed on every authenticated
   * transition instead — see {@link onAuthenticated} for why that, and not a boot-time repair, is
   * what keeps the backend and the browser agreeing on this device's endpoint.
   */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    void this.refreshStatus();
    // Permission can be withdrawn from site settings at any moment, with no gesture inside the app.
    this.watchPermissionChanges();
    // Every root service that caches per-user state (DateFormatService, NumberFormatService,
    // AvatarService, ToastDurationService, all via UserSettingStore) loads on this transition, and
    // for the same reason: it is the first moment a session exists that can reach an /app endpoint.
    // selectIsFullyAuthenticated rather than the raw isAuthenticated flag, deliberately — the flag
    // flips true the instant ANY token is applied, including the restricted forced-2FA-enrollment
    // one and the brief window on /auth/login before navigation, and POSTing a subscription with a
    // session that cannot reach the endpoint means a 401 the interceptor answers with
    // refresh-retry-teardown.
    this.store
      .select(selectIsFullyAuthenticated)
      .pipe(distinctUntilChanged())
      .subscribe((ready) => {
        if (ready) {
          void this.onAuthenticated();
        }
      });
    // The heartbeat that keeps a SHARED device's subscription alive: see {@link heartbeat} for why
    // this is the app's existing 401-driven token rotation and deliberately not a timer.
    this.tokenHolder.token$
      .pipe(filter((token): token is AuthToken => token !== null))
      .subscribe(() => {
        void this.heartbeat();
      });
  }

  /**
   * The false -> true authenticated transition: re-POST this device's subscription.
   *
   * This is the fix for the failure that made the feature self-disable after the first logout.
   * `removeCurrentSubscription()` DELETEs the backend row on teardown; before this existed nothing
   * ever POSTed again, because the boot-time repair only fired on `inactive` and the browser still
   * held a live subscription — so the next morning's login found one, called itself `active`, and
   * the device received nothing for the rest of its life while the icon said everything was fine.
   *
   * `register` is an idempotent upsert on the endpoint, so re-POSTing an endpoint the backend
   * already knows costs one request and changes nothing.
   *
   * MUST NOT PROMPT: gated on an already-granted permission exactly like `reactivate()` and
   * `refreshForCurrentIdentity()`. A user who has never been asked, or who has said no, is left
   * alone.
   */
  private async onAuthenticated(): Promise<void> {
    // Read independent of permission state, and before the permission gate below: a server with
    // push off is unavailable whether or not this device has ever been asked. checkCapability()
    // publishes 'unavailable' itself when it finds one, since nothing further would publish it.
    if (!(await this.checkCapability())) {
      return;
    }
    // READ THE STATUS HERE, with a session in hand. reportPermission() is skipped whenever nobody
    // is signed in -- the backend keys the record to the caller -- so the boot-time read in
    // init() reports NOTHING on a page that loads at /auth/login, which is every cold start. And
    // on the sign-in-without-reload path there is no other read at all: a second user on a shared
    // terminal would never have their answer reported. A DENIED device has no subscription, so
    // this report is the only way its refusal ever reaches the backend -- the whole reason the
    // permission record exists.
    await this.refreshStatus();
    // RE-READ `capable`, do not lean on checkCapability()'s return value. That method answers
    // `true` for a TRANSIENT failure by design -- "the question is still open" -- which is not the
    // same as "this server does push". On a server already known to have push off, one blipped key
    // fetch would otherwise fall through to subscribeAndPost() below, fail again on the same fetch
    // and publish `inactive`: a red "you are receiving nothing" the user cannot act on, in place of
    // the icon that was deliberately hidden, for the rest of the session. Same gate {@link
    // heartbeat} already applies.
    if (!this.capable || !isPushSupported() || Notification.permission !== 'granted') {
      return;
    }
    await this.subscribeAndPost();
  }

  /**
   * The heartbeat: fires on every `AccessTokenHolder.token$` emission, i.e. every access-token
   * rotation — the app's existing 401-driven refresh from `handle401`/`bootstrap()`, never a
   * timer. That is deliberate: `AuthRefreshService.refresh()` only runs when someone is actually
   * using the app, so an abandoned open tab stops rotating and stops heartbeating on its own,
   * which is exactly what must happen for a SHARED device (see the phase-2 design doc).
   *
   * Gated on {@link selectIsFullyAuthenticated}, read fresh at the moment of rotation rather than
   * cached from the authenticated-transition subscription: a rotation with no session, or during
   * a forced-2FA-enrollment session (a restricted token still rotates), must not re-POST.
   *
   * `register()` is an idempotent upsert on the endpoint, so this is a cheap no-op that merely
   * stamps `last_seen_at` on the backend row when nothing else has changed — including when
   * several tabs of the same origin each react to the same rotation.
   *
   * MUST NOT PROMPT, like every caller of {@link subscribeAndPost}: gated on an already-granted
   * permission.
   *
   * MUST NOT READ THE STORE SYNCHRONOUSLY. `tokenHolder.set(...)` — which fires `token$`, which
   * fires this method via the `init()` subscription — runs BEFORE the `store.dispatch(APPLY_SESSION,
   * ...)` that applies the corresponding identity/enrollment flag, in BOTH
   * `AuthRefreshService.applyToken` and `AuthService.applyToken`, synchronously, in that order,
   * within the same call stack. A synchronous `select(...).pipe(take(1))` here (NgRx state is
   * `BehaviorSubject`-backed, so a subscribe captures the CURRENT value instantly) would therefore
   * read PRE-dispatch state: a mid-session forced-2FA transition (an admin flips require-2FA while
   * the tab sits on `/app` — see `selectIsFullyAuthenticated` in `app-ready.selectors.ts`) would
   * still read `true` for one tick, pass the gate, and POST with the now-restricted bearer — which
   * the interceptor does NOT exempt on this endpoint (only the DELETE is carved out), so it 401s and
   * the catch below publishes a false `inactive` on an otherwise healthy device. Yielding to a
   * microtask first lets that dispatch — itself synchronous, issued later in the same caller — land
   * before the read. Same shape as the DynaConf sort-sanitiser's `queueMicrotask` past a synchronous
   * store notification (see CLAUDE.md). Pinned by the spec
   * `'reads the store AFTER the dispatch, even though the token fires first in one synchronous call
   * stack'`, which models the real ordering (rotation, then dispatch, both in one stack) rather than
   * two separately-settled steps.
   */
  private async heartbeat(): Promise<void> {
    await new Promise<void>((resolve) => queueMicrotask(resolve));
    const authenticated = await firstValueFrom(
      this.store.select(selectIsFullyAuthenticated).pipe(take(1)),
    );
    if (!authenticated) {
      return;
    }
    if (!this.capable || !isPushSupported() || Notification.permission !== 'granted') {
      return;
    }
    await this.subscribeAndPost();
  }

  /**
   * Whether this server has push switched on, checked against the live VAPID-key endpoint.
   *
   * Called from {@link onAuthenticated} (independent of permission state) and again from
   * {@link enable} (defence in depth, immediately before the one `requestPermission()` call in
   * the app) — the bug this exists to close let a user be prompted, and possibly permanently
   * blocked, for a feature the server never had switched on.
   *
   * Degrades silently: a TRANSIENT failure leaves the question open (returns `true`, i.e. does not
   * hide the icon) rather than declaring the server incapable on a blip.
   *
   * A DEFINITIVE failure does not. {@link ROUTE_ABSENT_STATUSES} are the statuses that mean the
   * route is not there at all — a backend built without the push endpoints answers 404, one that
   * routes them but has them switched off answers 501 — and reading either as "still open" is the
   * exact bug this gate exists to close, one status further in: `enable()` falls through to the
   * app's one `Notification.requestPermission()` call and the user can be left permanently
   * `denied` for a feature the server never had. The empty-key case was only ever HALF the answer,
   * because it presumes a server that has the endpoint at all. Secondary effect of getting this
   * wrong: `capabilityResolved` never flips, so EVERY Enable press first awaits a failing round
   * trip — and an awaited fetch spends the transient user activation `requestPermission()` needs
   * in Safari, so the prompt is refused and the modal snaps back to `off` saying nothing.
   */
  private async checkCapability(): Promise<boolean> {
    if (!isPushSupported()) {
      // A browser limitation is a different, unrelated reason and keeps its own 'unsupported'
      // status — no need to ask the server.
      return true;
    }
    let key: string;
    try {
      key = await this.fetchVapidKey();
    } catch (e) {
      if (e instanceof HttpErrorResponse && ROUTE_ABSENT_STATUSES.includes(e.status)) {
        console.warn('[push] server has no push endpoints (HTTP ' + e.status + ') — feature off');
        // Answer it, the same way an empty key does, so the icon hides AND `enable()`'s gate can
        // read the settled answer synchronously instead of paying for this round trip again.
        this.noteCapability('');
        this.publish('unavailable');
        return false;
      }
      console.warn('[push] capability check failed', e);
      return true;
    }
    if (!this.noteCapability(key)) {
      this.publish('unavailable');
    }
    return this.capable;
  }

  /**
   * Read this server's VAPID key, reusing a read taken moments ago.
   *
   * The two callers -- {@link checkCapability} and {@link trySubscribeAndPost} -- run back to back
   * on every login, so the identical key was fetched twice per authenticated transition, per tab.
   * Anything older than {@link VAPID_KEY_FRESH_MS} is re-read, which is what keeps a server-side
   * rotation self-healing; read that constant before widening the window.
   */
  private async fetchVapidKey(): Promise<string> {
    const cached = this.vapidKeyCache;
    if (cached && Date.now() - cached.at < VAPID_KEY_FRESH_MS) {
      return cached.key;
    }
    const fetched = await firstValueFrom(this.api.vapidKey());
    this.vapidKeyCache = { key: fetched, at: Date.now() };
    return fetched;
  }

  /**
   * Record what a fetched VAPID key says about this server. Shared by the two places that read the
   * key, so a key fetched on the subscribe path answers {@link enable}'s gate just as well as one
   * fetched by {@link checkCapability} -- and neither can learn the answer without remembering it.
   */
  private noteCapability(key: string): boolean {
    this.capable = key !== '';
    this.capabilityResolved = true;
    return this.capable;
  }

  /**
   * Tell the backend what this browser answered, when the answer CHANGES.
   *
   * <p>This is the only way a refusal is ever visible: a denied browser has no subscription, so
   * nothing else in the system knows it exists — a user with a working phone and a laptop that
   * said no looks perfectly healthy. Reported for every state, not only denial, because granting
   * after a refusal has to clear the record; otherwise an admin chases someone who fixed it days
   * ago.
   *
   * <p>Deduplicated against the last reported value, and skipped entirely when nobody is signed
   * in. Without both, this would POST on every status read — and a status read happens on every
   * navigation, token rotation and permission check.
   *
   * <p>Fire-and-forget: a failure here changes nothing the user can see, and must never surface.
   */
  private reportPermission(permission: NotificationPermission): void {
    if (!isPushSupported() || this.lastReportedPermission === permission) {
      return;
    }
    if (!this.tokenHolder.get()) {
      // No session: the backend keys the permission record to the caller, so there is nobody to attribute this
      // to. The next status read after login reports it.
      return;
    }
    this.lastReportedPermission = permission;
    // Truncated through the SHARED helper -- see PUSH_LABEL_MAX_CHARS for why the two writers of
    // this column must agree on the length.
    this.api.reportPermission(getPushDeviceId(), permission, deviceLabel()).subscribe({
      error: () => {
        // Let the next change try again rather than silently never reporting.
        this.lastReportedPermission = null;
      },
    });
  }

  /**
   * Drop this device's delivery details after permission was withdrawn, if there is anything to drop.
   *
   * <p>Guarded on actually holding an endpoint so the common case -- a user who has never enabled
   * push, where `Notification.permission` is `default` on every single status read -- issues no
   * request at all.
   */
  private dropDeliveryAfterPermissionLoss(): void {
    // `deliveryRemovalOwed` is the third reason there is something to do: a removal this device
    // already attempted and could not land leaves the backend delivering, with both endpoints
    // cleared and nothing else to notice it. See the field.
    if (!this.currentEndpoint && !this.postedEndpoint && !this.deliveryRemovalOwed) {
      return;
    }
    this.removeCurrentSubscription();
  }

  /**
   * React to permission being withdrawn WHILE THE APP IS OPEN.
   *
   * <p>`navigator.permissions.query({ name: 'notifications' })` gives a live PermissionStatus whose
   * `change` event fires when the user flips the switch in site settings -- the only way to learn
   * about it without waiting for the next status read. Everything else is a fallback: the check in
   * {@link refreshStatus} catches a revocation made while the app was closed, and the push
   * service's own 410 is the last backstop for a device that never opens the app again.
   *
   * <p>Best-effort by construction: the API is absent in older Safari, and even where present the
   * `notifications` name is not universally supported, so a rejected query is swallowed. Losing
   * this listener costs promptness, never correctness.
   *
   * <p>`zone.run` for the same reason as the Web Lock callback: a PermissionStatus event is not
   * something zone.js is guaranteed to patch, and the status publish that follows has to schedule
   * change detection or the header icon keeps showing green over a revoked permission.
   */
  private watchPermissionChanges(): void {
    const permissions = (navigator as unknown as Record<string, any>)['permissions'];
    if (!permissions || typeof permissions['query'] !== 'function') {
      return;
    }
    let query: Promise<any>;
    try {
      query = permissions['query']({ name: 'notifications' });
    } catch (e) {
      return;
    }
    void query
      .then((status: any) => {
        if (!status || typeof status.addEventListener !== 'function') {
          return;
        }
        status.addEventListener('change', () => {
          this.zone.run(() => {
            if (status.state !== 'granted') {
              this.dropDeliveryAfterPermissionLoss();
            }
            void this.refreshStatus();
          });
        });
      })
      .catch(() => undefined);
  }

  /** Re-read permission + subscription and publish the resulting status. */
  async refreshStatus(): Promise<PushStatus> {
    if (!isPushSupported()) {
      return this.publish('unsupported');
    }
    if (!this.capable) {
      return this.publish('unavailable');
    }
    const permission = Notification.permission;
    if (permission !== 'granted') {
      // Permission is gone -- revoked in site settings, or reset. The browser has already invalidated
      // the push subscription, so the backend row is now a dead endpoint: it would be pushed to on
      // every event until the push service happens to answer 410 (the only status the sender treats
      // as terminal), and on a personal row nothing else ever reaps it.
      //
      // Dropping it HERE rather than only in the permission-change listener is what covers the
      // revocation that happened while the app was closed, and every browser whose Permissions API
      // does not fire a change event. Costs one DELETE, once: removeCurrentSubscription() clears
      // the endpoint it works from, so a second pass through this branch issues nothing.
      this.dropDeliveryAfterPermissionLoss();
      // REPORTED AFTER THE DROP, NOT BEFORE IT. `removeCurrentSubscription()` clears
      // `lastReportedPermission` on purpose (the dedup is a per-SESSION fact — see the field), and
      // reaching it through the drop above is the one path where the clear lands on a report this
      // same pass has just made: the field was set, then wiped, so the next status read POSTed the
      // identical answer a second time. Reporting afterwards leaves the dedup standing, and the
      // report itself is unaffected by the removal — it carries the device id and the permission,
      // neither of which the drop touches.
      this.reportPermission(permission);
      return this.publish(permission === 'denied' ? 'blocked' : 'off');
    }
    this.reportPermission(permission);
    const subscription = await this.existingSubscription();
    this.currentEndpoint = subscription ? subscription.endpoint : null;
    // `active` means the BACKEND knows this endpoint, not merely that the browser holds one --
    // see PUSH_POSTED_ENDPOINT_STORAGE_KEY for the three ways those come apart.
    const backendKnowsIt = subscription !== null && subscription.endpoint === this.postedEndpoint;
    return this.publish(backendKnowsIt ? 'active' : 'inactive');
  }

  /**
   * THE ONLY `Notification.requestPermission()` CALL SITE IN THE APPLICATION.
   *
   * Reached only from the Enable button in PushStatusDialogComponent. A prompt fired
   * from anywhere else can permanently cost a device: `denied` cannot be re-prompted
   * by any browser, and on iOS the only reset is deleting and re-adding the Home
   * Screen icon. If you are adding a second call site, you are introducing a bug.
   */
  async enable(): Promise<PushStatus> {
    if (!isPushSupported()) {
      return this.publish('unsupported');
    }
    if (Notification.permission === 'denied') {
      return this.publish('blocked');
    }
    // The capability gate, immediately before the one requestPermission() call in the app —
    // defence in depth, so a future caller reaching enable() directly cannot prompt on a server
    // with push off. onAuthenticated() already fetched the key earlier in this session, and this
    // reads that ANSWER rather than fetching it again.
    //
    // THAT IS NOT AN OPTIMISATION. `Notification.requestPermission()` requires TRANSIENT USER
    // ACTIVATION, and the activation belongs to the click that reached this method: awaiting an
    // HTTP round trip (GET /v2/push/vapid-key) in between spends it. On a slow link — or on the
    // Home Screen Safari this feature exists for, the strictest implementation there is — the
    // activation had expired by the time the prompt was asked for, so the call rejected, the catch
    // below degraded to refreshStatus(), and the modal snapped back to `off` saying nothing. The
    // user then retries into exactly the same trap.
    //
    // The fetch is only paid for when there is genuinely nothing to read: `capable` starts `true`
    // as "nobody has asked yet", so trusting it unresolved would be the field bug back (prompted,
    // possibly permanently blocked, for a feature the server never had switched on). A caller that
    // reaches here before any authenticated transition — or after a capability check that failed
    // on the network, which deliberately leaves the question open — still awaits the check and
    // still cannot prompt on an incapable server. checkCapability() has already published
    // 'unavailable' when it finds one, so the current status IS the return value.
    if (this.capabilityResolved) {
      if (!this.capable) {
        return this.publish('unavailable');
      }
    } else if (!(await this.checkCapability())) {
      return this.statusSubject.value;
    }
    let permission: NotificationPermission;
    try {
      permission = await Notification.requestPermission();
    } catch (e) {
      // A Permissions-Policy restriction (or similar) can reject this outright. Degrade to
      // whatever the browser's permission actually is rather than throwing out of a button
      // handler — never block the app.
      console.warn('[push] requestPermission failed', e);
      return this.refreshStatus();
    }
    // REPORTED HERE, AND THIS IS THE ONLY PLACE THE ANSWER IS KNOWABLE ON SOME BROWSERS.
    //
    // The two call sites in refreshStatus() cover a permission read; watchPermissionChanges()
    // covers a flip made in site settings. Neither reaches Safari -- including the Home Screen
    // Safari this feature exists for -- because `navigator.permissions.query({name:'notifications'})`
    // is absent there, so the watcher bails out and the only remaining reader is the next status
    // read, which happens at the NEXT login. Without this the admin's compliance panel said
    // "Mai richieste" about a user who had actively refused, for the rest of the session: the icon
    // went red locally and the backend was never told. Reported for a grant too -- a `default` row
    // left standing over a working device is the same lie in the other direction.
    //
    // Deduplicated and session-gated inside reportPermission(), so this costs nothing when the
    // answer has not moved. No drop of the delivery details here, unlike refreshStatus(): reaching
    // the prompt at all means permission was `default`, and any delivery a previous grant left
    // behind was already dropped by the status read that observed it.
    if (permission !== 'granted') {
      this.reportPermission(permission);
      return this.publish(permission === 'denied' ? 'blocked' : 'off');
    }
    this.reportPermission(permission);
    return this.subscribeAndPost();
  }

  /** Silent re-subscribe. No prompt — permission is already granted. */
  async reactivate(): Promise<PushStatus> {
    if (!isPushSupported() || Notification.permission !== 'granted') {
      return this.refreshStatus();
    }
    return this.subscribeAndPost();
  }

  /**
   * Fire-and-forget teardown hook. clearSession() is synchronous and cannot await;
   * the DELETE is carved out of the interceptor's 401 handling so a dead session
   * cannot make it re-enter teardown. Uses the cached endpoint rather than reading
   * the subscription, because that read is async and clearSession will not wait.
   *
   * The BROWSER subscription is dropped alongside the backend row. Deleting only the row left the
   * two disagreeing — the browser kept a live subscription the backend had never heard of — and
   * that disagreement is what made the whole feature self-disable after the first logout. The
   * posted-endpoint record is cleared for the same reason: after this, `active` is no longer true.
   */
  removeCurrentSubscription(): void {
    // FIRST, before anything awaits: any subscribe/POST already in flight is now working for a
    // user who has logged out and must abandon its result rather than apply it (see {@link epoch}).
    this.epoch++;
    this.currentEndpoint = null;
    this.setPostedEndpoint(null);
    // The attempt the epoch bump above has just condemned must not be JOINED by the next caller.
    // Dropping the epoch alone left it advertised: a login right after a logout had both the
    // authenticated transition and the heartbeat coalesce onto the pending promise, which then hit
    // `abandoned()`, registered nothing and answered with the current status — so the new session
    // had no backend row and nothing to re-POST until the next token rotation. The abandoned
    // attempt still settles harmlessly; its `finally` only nulls the field when it is still the
    // one advertised, so it cannot clobber the successor.
    this.inFlightSubscribe = null;
    // Dropped at the session boundary for the same reason the window is short: the next session
    // must not mint a subscription against a key this one happened to read.
    this.vapidKeyCache = null;
    // The permission dedup is a per-SESSION fact — see the field. Clearing it is what lets the
    // next user on this machine report their own answer.
    this.lastReportedPermission = null;
    // A BROWSER THAT CANNOT DO PUSH HAS NOTHING TO TEAR DOWN. This runs from `clearSession()`, i.e.
    // on EVERY logout and every 401 teardown, for every user — and on a browser with no
    // `PushManager` (an iOS Safari tab) neither step below can do anything: there is no browser
    // subscription to drop, and `reportPermission` refuses to run at all there, so this device has
    // never registered a row for the DELETE to clear. What they DO produce is noise —
    // `unsubscribeLocally()` reaches `ensureRegistration()`, whose failure is swallowed with a
    // `console.warn` on every single logout — and one pointless authenticated request that also
    // mints `push.deviceId` into localStorage on its way past `getPushDeviceId()`.
    //
    // THE GUARD STOPS THERE, DELIBERATELY. It is tempting to skip a supported browser that holds no
    // endpoint either, the way {@link dropDeliveryAfterPermissionLoss} does — but that test is
    // wrong here. The DELETE names the DEVICE, not an endpoint, and a device row exists for every
    // signed-in user on a supported browser (see {@link reportPermission}, which reports `default`
    // too). So "this tab does not know an endpoint" does not mean "this device has no delivery":
    // `push.postedEndpoint` is written through `safe-storage`, whose refusals are SWALLOWED, and a
    // tab that lost that write would skip the DELETE and leave the row delivering to a session that
    // has ended. The state resets above stay unconditional regardless — the epoch bump is what makes
    // an in-flight attempt abandon.
    if (!isPushSupported()) {
      return;
    }
    // Not awaited (the caller is synchronous) and it can never reject — see unsubscribeLocally.
    void this.unsubscribeLocally();
    if (this.deliveryRemoved) {
      // Already dropped, nothing registered since: a second DELETE would change nothing on the
      // server, and on the logoutEverywhere() path it is issued after tokens_valid_from has been
      // bumped, so it is a guaranteed 401 the interceptor carve-out swallows in silence.
      return;
    }
    this.deliveryRemoved = true;
    // By DEVICE, unconditionally: the server clears the delivery half of a row keyed by this
    // browser, so there is no endpoint to have on hand and nothing to skip when we do not. The row
    // itself survives, carrying what this browser answered when asked for permission.
    this.api.unsubscribe(getPushDeviceId()).subscribe({
      next: () => {
        this.deliveryRemovalOwed = false;
      },
      // A removal that never landed has removed nothing — let the next call try again rather than
      // skip on the strength of an attempt that failed. `deliveryRemovalOwed` is what gives that
      // next call a reason to run at all once the endpoints this one cleared are gone.
      error: () => {
        this.deliveryRemoved = false;
        this.deliveryRemovalOwed = true;
      },
    });
  }

  /**
   * After a proven identity switch. Silent: browser permission is scoped to the
   * ORIGIN, not the user, so an already-granted device needs no gesture. If
   * permission is not granted, this does nothing and NO PROMPT APPEARS.
   *
   * Retried, unlike every other caller: until the POST lands, the backend row still maps this
   * device to the OUTGOING user, so their notifications arrive on the incoming user's lock screen
   * carrying the outgoing user's record identity. There is no later event to repair that within
   * this session — the authenticated transition already fired for this identity.
   *
   * @param enrollmentRequired whether the INCOMING identity is confined to the forced-2FA wizard.
   *        PASSED IN RATHER THAN READ FROM THE STORE, and required rather than defaulted. Both
   *        callers hold the flag literally in scope — each hands it to `leaveAfterSwitch(...)` on
   *        the line above — while a store read here would have to answer the ordering question
   *        {@link heartbeat} answers with a `queueMicrotask`: on the refresh path `tokenHolder.set`
   *        fires before the APPLY_SESSION dispatch, so a synchronous select reads pre-dispatch
   *        state. No default, so a third caller cannot inherit the bug by omission.
   *
   *        Gating on it matters for the same reason {@link heartbeat} gates on
   *        {@link selectIsFullyAuthenticated}: an enrollment token is scoped to
   *        `/v2/profile/2fa/**`, `/v2/push/devices` is carved OUT of the interceptor's 401
   *        refresh-retry, and nothing else rescues it — so all three attempts 401 two seconds apart
   *        and the last publishes `inactive` ("you are not receiving anything") on a device that is
   *        fine. The stale row is left pointing at the outgoing user until the wizard is finished,
   *        which fires an authenticated transition of its own and re-registers.
   */
  refreshForCurrentIdentity(enrollmentRequired: boolean): void {
    if (enrollmentRequired) {
      return;
    }
    if (!isPushSupported() || Notification.permission !== 'granted') {
      return;
    }
    // WAIT FOR A BEARER RATHER THAN POST WITHOUT ONE. The two callers reach this method in
    // opposite states: `AuthRefreshService.applyToken` has just SET the incoming user's own valid
    // token, while `AuthSyncService.handle` has just CLEARED the outgoing one on purpose (that
    // clear is what hands the tab over — see there). POSTing from the second state sends
    // `/v2/push/devices` out with no Authorization header at all, and that endpoint is carved out
    // of the interceptor's 401 refresh-retry on purpose (the heartbeat would re-feed itself
    // through refresh — see `pushDevicesEndpoint`), so nothing rescues it: all three attempts 401,
    // and the last one publishes `inactive` — "you are not receiving anything" — on a device that
    // is fine. Moving the call above the clear instead would be worse: the POST would land under
    // the OUTGOING user, which is the exact row this path exists to re-point.
    //
    // So arm a one-shot on the next non-null token instead. On a tab routed into /app the bearer
    // arrives within one 401+refresh of the landing fetch; on one parked at /auth/login no bearer
    // may arrive at all, and doing nothing until it does is precisely right — there is no identity
    // to register under yet, and the stale row is left exactly as it was rather than reported
    // broken.
    if (this.tokenHolder.get()) {
      void this.subscribeAndPost(IDENTITY_SWITCH_ATTEMPTS);
      return;
    }
    this.refreshOnNextBearer();
  }

  /**
   * Arm a one-shot re-registration for the moment a bearer next appears.
   *
   * Epoch-guarded like every other deferred step here: a teardown landing between the arming and
   * the token means the switch this was armed for is gone, and re-registering there would put a
   * row back for a user who has signed out. Only ONE arm is ever outstanding — a second switch
   * before the first bearer arrives replaces it, since both want the same thing and the later
   * identity is the live one.
   */
  private refreshOnNextBearer(): void {
    const epoch = this.epoch;
    this.pendingBearerRefresh?.unsubscribe();
    this.pendingBearerRefresh = this.tokenHolder.token$
      .pipe(
        filter((token): token is AuthToken => token !== null),
        take(1),
      )
      .subscribe(() => {
        this.pendingBearerRefresh = null;
        if (this.abandoned(epoch)) {
          return;
        }
        void this.subscribeAndPost(IDENTITY_SWITCH_ATTEMPTS);
      });
  }

  /**
   * PATCHes this device's personal/shared mode and updates {@link shared$} on success. The
   * Task 4 modal toggle is its only caller.
   *
   * SETTABLE WITH NO SUBSCRIPTION AT ALL. It used to be a silent no-op without a known endpoint,
   * because the endpoint was how a device was named; the PATCH names the DEVICE now, and `shared`
   * says "anyone can walk up to this terminal" — which is true of a browser that has not
   * subscribed, and truest of one that refused. Do not re-add an endpoint guard here.
   *
   * Degrades silently on failure like every other public method here — a transient network error
   * must not throw out of a toggle handler. On failure the published mode is left exactly as it
   * was: the PATCH did not take effect, so claiming it did would tell the user something false.
   *
   * RETURNS WHETHER THE MODE WAS ACTUALLY PERSISTED, and the caller must act on it. Leaving the
   * field alone is not enough to leave the UI alone: `MatSlideToggle` flips its OWN internal
   * `checked` on the user's gesture, and a `[checked]="shared"` binding whose expression did not
   * change is never re-evaluated — so a swallowed failure left the toggle reading "Dispositivo
   * condiviso" over a backend that still had `shared = false`, i.e. a warehouse terminal
   * permanently exempt from the TTL, asserted by a control nobody had reason to doubt. The one
   * falsy path — a caught failure — answers `false`.
   */
  async setShared(shared: boolean): Promise<boolean> {
    try {
      await firstValueFrom(this.api.setShared(getPushDeviceId(), shared));
      this.sharedSubject.next(shared);
      return true;
    } catch (e) {
      console.warn('[push] setShared failed', e);
      return false;
    }
  }

  /**
   * Subscribe (or reuse) and POST to the backend. Never prompts, never rejects.
   *
   * THE EPOCH IS CAPTURED HERE, NOT PER ATTEMPT. It used to be captured inside
   * {@link trySubscribeAndPost}, which meant every retry re-read the CURRENT epoch and so could
   * never notice a teardown that had landed during the `pause` between attempts: the next attempt
   * minted a subscription, POSTed it and published `active` for a user who had logged out — the
   * same failure the guard exists to close, one path over. Only reachable where `attempts > 1`,
   * i.e. from `refreshForCurrentIdentity()` (3 attempts, 2s apart), which is a wide window.
   *
   * @param attempts how many times to try before giving up and publishing `inactive`.
   */
  private subscribeAndPost(attempts = 1): Promise<PushStatus> {
    // COALESCED, for any two callers that genuinely overlap: the second wants exactly what the
    // first is already doing, and a subscription is per browser install, so there is never a
    // reason to run two at once.
    //
    // IT NO LONGER CATCHES THE LOGIN PAIR, and that is worth stating because the comment here used
    // to claim it did. Logging in still fires two independent triggers -- the authenticated
    // transition and the heartbeat's first token$ emission -- but `onAuthenticated()` now awaits
    // `checkCapability()` (a full GET /v2/push/vapid-key) and `refreshStatus()` before it reaches
    // this method, by which point the heartbeat's attempt has normally settled and the field is
    // back to null. So a login costs a second complete ensureRegistration + vapidKey +
    // getSubscription + POST cycle. Left alone deliberately: registration upserts on the stable
    // device id, so the two land on one row however many endpoints they mint between them, and the
    // ordering that produces the miss (capability BEFORE any POST) is the fix for a real
    // field-found bug. What is at stake is a wasted round trip per login, not a duplicate row --
    // which is also why the cross-tab Web Lock that used to sit here is gone.
    //
    // A RETRYING call (attempts > 1) is NOT coalesced. Its only caller is the identity switch,
    // where the in-flight attempt may belong to the OUTGOING user -- joining it would leave the
    // backend row pointing at the wrong identity, which is the one thing that path exists to fix.
    if (this.inFlightSubscribe && attempts === 1) {
      return this.inFlightSubscribe;
    }
    // A RETRYING call does not JOIN -- but it is still JOINABLE, and it does not RUN CONCURRENTLY
    // either. Three separate properties, and the first two rounds each fixed one and left the third.
    //
    // Not joining: its only caller is the identity switch, and joining an in-flight attempt would
    // hand it a RESULT that may belong to the OUTGOING user, which is the one thing that path
    // exists to fix. That is the `attempts === 1` clause above.
    //
    // Being joinable: returning WITHOUT advertising the run meant that on the refresh path
    // `tokenHolder.set(...)` fires `token$` -> heartbeat, and the very next statements call
    // `refreshForCurrentIdentity()`, so the heartbeat's deferred continuation reached this method,
    // found the field still null and started a SECOND run. That is the `this.inFlightSubscribe =
    // run` at the bottom.
    //
    // Not running concurrently: THE SAME COLLISION SURVIVED IN THE OTHER ORDER. With a heartbeat
    // POST already in flight, a cross-tab `login` broadcast arriving a moment later starts the
    // retrying run immediately AND overwrites the field, so the two overlap anyway -- only the
    // ordering decided whether the bug reproduced. Two overlapping `pushManager.subscribe()` calls
    // and two POSTs against one browser subscription is the hazard either way: after a VAPID
    // rotation both take the unsubscribe-then-subscribe branch in `subscriptionFor`, where one can
    // throw `InvalidStateError` or leave `postedEndpoint` naming an endpoint the backend did not
    // last store.
    //
    // So a retrying run QUEUES BEHIND whatever is in flight instead of racing it. It still returns
    // its OWN result -- the outgoing user's answer is discarded, never adopted -- which is what
    // keeps the not-joining property intact. `catch` before `then` because the predecessor's
    // failure is not this run's concern and must not skip it; `zone.run` is re-entered inside the
    // continuation because a `.then()` callback does not inherit the caller's zone.
    //
    // AND THE EPOCH IS CAPTURED HERE, BEFORE THAT QUEUE'S AWAIT -- not inside the run. Reading it
    // in `runSubscribeAndPost` means a retrying run reads it only after its predecessor has
    // settled, so a teardown landing DURING the predecessor is invisible to it: it mints a fresh
    // browser subscription (teardown's own `unsubscribeLocally()` is what makes `subscriptionFor`
    // mint one), POSTs it up to `attempts` times for a signed-out session and publishes a false
    // `active`/`inactive`. It also restores the not-running-concurrently property that
    // `removeCurrentSubscription()` can otherwise break: that method nulls `inFlightSubscribe` but
    // cannot detach an already-chained `.then(begin)`, so a login landing right afterwards starts a
    // second run alongside the queued one -- with the epoch read here the queued one abandons
    // immediately instead of racing it at the `pushManager`.
    const epoch = this.epoch;
    const previous = attempts > 1 ? this.inFlightSubscribe : null;
    const begin = (): Promise<PushStatus> =>
      this.zone.run(() => this.runSubscribeAndPost(attempts, epoch));
    const run: Promise<PushStatus> = (previous ? previous.catch(() => undefined).then(begin) : begin())
      .finally(() => {
        if (this.inFlightSubscribe === run) {
          this.inFlightSubscribe = null;
        }
      });
    this.inFlightSubscribe = run;
    return run;
  }


  /**
   * The actual attempt. Never call directly -- go through {@link subscribeAndPost}.
   *
   * @param epoch the value {@link subscribeAndPost} captured when the call was MADE. Not re-read
   *        here: a retrying run may have spent that whole time queued behind a predecessor, and a
   *        teardown landing in the queue must abandon it exactly as one landing mid-attempt does.
   */
  private async runSubscribeAndPost(attempts: number, epoch: number): Promise<PushStatus> {
    // The queue is an await like any other, and the widest one on this path.
    if (this.abandoned(epoch)) {
      return this.statusSubject.value;
    }
    for (let attempt = 1; ; attempt++) {
      try {
        return await this.trySubscribeAndPost(epoch);
      } catch (e) {
        // A teardown landing while this attempt was failing leaves nothing to retry FOR, and
        // nothing true to publish — removeCurrentSubscription has already left the status honest.
        if (this.abandoned(epoch)) {
          return this.statusSubject.value;
        }
        // A failed attempt is the one moment a just-read key is worth doubting -- a rotation that
        // landed between the read and the mint is exactly how `subscribe()` throws here. Drop it so
        // the retry, and the next caller, pay for a fresh read rather than repeating the failure
        // with the same value.
        this.vapidKeyCache = null;
        if (attempt >= attempts) {
          // Degrade silently and tell the truth in the icon. Never block the app.
          console.warn('[push] subscribe failed', e);
          return this.publish('inactive');
        }
        console.warn(`[push] subscribe failed (attempt ${attempt} of ${attempts}), retrying`, e);
        await this.pause(this.retryDelayMs);
        // The pause is the widest await in the whole path (seconds, not a round trip), so it is
        // the likeliest place for a teardown to land. Check it before spending another attempt.
        if (this.abandoned(epoch)) {
          return this.statusSubject.value;
        }
      }
    }
  }

  /**
   * One attempt. Throws on failure so {@link subscribeAndPost} can decide whether to retry.
   *
   * Every await boundary past the first is followed by an {@link epoch} check: a teardown landing
   * mid-attempt must abandon the attempt rather than let it finish re-registering a device for a
   * logged-out user. An abandoned attempt does NOT throw — there is nothing to retry, the session
   * is gone — and it does not publish, because {@link removeCurrentSubscription}'s own path
   * already leaves the status telling the truth.
   *
   * @param epoch the value captured by {@link subscribeAndPost} when the WHOLE call began — not
   *        re-read here, or a retry would silently forgive a teardown that landed between attempts.
   */
  private async trySubscribeAndPost(epoch: number): Promise<PushStatus> {
    const registration = await this.ensureRegistration();
    const key = await this.fetchVapidKey();
    if (this.abandoned(epoch)) {
      // Nothing minted yet, so nothing to undo.
      return this.statusSubject.value;
    }
    if (!this.noteCapability(key)) {
      // The backend has no VAPID key configured — this server does not do push. Retrying cannot
      // help, so this is not thrown. Not 'inactive': that copy tells the user their DEVICE is
      // broken and offers a Reactivate that repeats this exact GET forever. Reachable here as the
      // fallback for callers that skip checkCapability() (reactivate(), refreshForCurrentIdentity()),
      // so this stays in step with the field-found bug's fix regardless of entry point.
      return this.publish('unavailable');
    }
    const applicationServerKey = urlBase64ToUint8Array(key);
    const subscription = await this.subscriptionFor(registration, applicationServerKey);
    if (this.abandoned(epoch)) {
      // A subscription may have been MINTED here — teardown's own local unsubscribe had already
      // run, which is precisely what makes `subscriptionFor` mint one — so drop it. A live browser
      // subscription the backend has never heard of is the disagreement phase 1 closed.
      void this.unsubscribeLocally();
      return this.statusSubject.value;
    }
    const json = subscription.toJSON() as {
      endpoint?: string;
      keys?: { p256dh: string; auth: string };
    };
    const heldSecret = getPushDeviceSecret();
    const body: PushSubscriptionBody = {
      deviceId: getPushDeviceId(),
      endpoint: subscription.endpoint,
      keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
      label: deviceLabel(),
      // Proof that this browser owns the row its device id names. Omitted, never blank, when there
      // is none to present — the id alone is what the backend treats as a stranger's claim, and it
      // still allows one for a brand-new row or for a row this user already owns.
      ...(heldSecret ? { deviceSecret: heldSecret } : {}),
    };
    // Learn this device's mode from the response itself — no second read, no second source of
    // truth (see shared$).
    const response = await firstValueFrom(this.api.subscribe(body));
    // BEFORE the epoch guard, deliberately. The secret is returned exactly once, and the abandoned
    // branch below only DELETES the delivery half of the row — the row itself survives carrying the
    // hash of this secret, so dropping it here would leave the next registration unable to prove a
    // possession the server still records. A no-op when the response minted nothing.
    setPushDeviceSecret(response.deviceSecret);
    if (this.abandoned(epoch)) {
      // The delivery details landed under the outgoing user. Teardown's own DELETE fired before this
      // POST did, so it dropped the OLD endpoint (or none) and this one would outlive the session —
      // try to undo it here. Fire-and-forget and error-swallowed, exactly like the teardown DELETE it
      // repeats, and carved out of the interceptor's 401 handling for the same reason.
      //
      // WHETHER IT CAN LAND DEPENDS ON WHICH TEARDOWN ABANDONED US, and on the logout path it cannot.
      // `AuthService.clearSession()` runs `removeCurrentSubscription()` and THEN `tokenHolder.clear()`,
      // both synchronously, so by the time this response arrives the holder is empty: `addContext` adds
      // no Authorization header, the DELETE 401s, and the carve-out swallows it in silence. The other
      // callers of `removeCurrentSubscription()` — the permission-loss drop, and the identity switch's
      // own re-registration — run with a live bearer and this does land.
      //
      // So the state is corrected rather than assumed: there IS delivery on the row again, whatever
      // this request does, and saying otherwise would let the NEXT teardown skip its own DELETE on the
      // strength of a removal that this POST has since undone. `deliveryRemoved` goes back to false
      // first and only returns to true if the server actually answers. Where the DELETE cannot land,
      // the delivery half survives until the push service 410s the endpoint this browser has just
      // unsubscribed from locally, or the TTL sweep collects it.
      this.deliveryRemoved = false;
      // BUT NOT WHEN A SUCCESSOR HAS ALREADY REGISTERED. The compensation names the DEVICE, not the
      // endpoint this attempt posted, and `unsubscribeLocally()` drops whatever subscription the
      // browser currently holds -- so on a shared terminal where the next user has signed in and
      // completed their own registration while this POST was outstanding, both would tear down
      // THEIR row and THEIR subscription, after they had already published `active`. The
      // `deliveryRemoved = true` on success would then make their teardown skip its own DELETE too.
      // `postedEndpoint` is cleared by every teardown and written only by a completed registration,
      // so a non-null value here means exactly "something has been registered since we were
      // abandoned". Leaving the delivery half in place is the safe half of the trade: it is the
      // successor's own device row, and this browser is the successor's browser.
      if (this.postedEndpoint !== null) {
        return this.statusSubject.value;
      }
      this.api.unsubscribe(getPushDeviceId()).subscribe({
        next: () => {
          this.deliveryRemoved = true;
          this.deliveryRemovalOwed = false;
        },
        // Same reading as the teardown DELETE: this one routinely cannot land (see above), and the
        // delivery half it failed to drop is exactly what the next status read has to retry.
        error: () => {
          this.deliveryRemovalOwed = true;
        },
      });
      void this.unsubscribeLocally();
      return this.statusSubject.value;
    }
    this.currentEndpoint = subscription.endpoint;
    this.setPostedEndpoint(subscription.endpoint);
    // There is delivery to drop again. Without this the next teardown would skip its DELETE on the
    // strength of a removal that a later registration has since undone. Nothing is OWED any more
    // either: whatever an earlier removal failed to drop, this device is deliberately registered
    // now, and retrying that removal would tear down the registration this call just made.
    this.deliveryRemoved = false;
    this.deliveryRemovalOwed = false;
    this.sharedSubject.next(response.shared);
    return this.publish('active');
  }

  /** Whether a teardown has run since the attempt that captured `epoch` began. */
  private abandoned(epoch: number): boolean {
    if (epoch === this.epoch) {
      return false;
    }
    console.warn('[push] subscribe abandoned — the session was torn down mid-flight');
    return true;
  }

  /**
   * The subscription to POST: the one the browser already holds, unless it was created with a
   * DIFFERENT VAPID key.
   *
   * Fetching the key instead of bundling it was chosen precisely so it could be rotated; reusing
   * whatever the browser holds threw that away. After a rotation every subscribed device kept its
   * old-key subscription, re-POSTed it, published `active`, and the push service then refused the
   * backend's VAPID-mismatched sends — a whole fleet silently receiving nothing.
   *
   * The subscription has to be dropped before re-subscribing: `subscribe()` with a different
   * `applicationServerKey` throws `InvalidStateError` while one exists.
   */
  private async subscriptionFor(
    registration: ServiceWorkerRegistration,
    applicationServerKey: Uint8Array,
  ): Promise<PushSubscription> {
    const existing = await registration.pushManager.getSubscription();
    if (existing && !createdWithKey(existing, applicationServerKey)) {
      try {
        await existing.unsubscribe();
      } catch (e) {
        // Already gone as far as the push service is concerned; subscribing below still works.
        console.warn('[push] unsubscribe before a VAPID key change failed', e);
      }
      this.setPostedEndpoint(null);
      return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
    }
    return (
      existing ??
      (await registration.pushManager.subscribe({
        // Mandatory on Chrome: a promise that every push shows something visible.
        userVisibleOnly: true,
        applicationServerKey,
      }))
    );
  }

  /**
   * Drop the browser's own subscription on teardown. Never rejects: it is called without `await`
   * from a synchronous `clearSession()`, so a rejection here would surface as an unhandled one.
   */
  private async unsubscribeLocally(): Promise<void> {
    try {
      const subscription = await this.existingSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    } catch (e) {
      console.warn('[push] local unsubscribe during teardown failed', e);
    }
  }

  private async ensureRegistration(): Promise<ServiceWorkerRegistration> {
    if (!this.registration) {
      this.registration = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
      await navigator.serviceWorker.ready;
    }
    return this.registration;
  }

  private async existingSubscription(): Promise<PushSubscription | null> {
    try {
      const registration = await this.ensureRegistration();
      return await registration.pushManager.getSubscription();
    } catch (e) {
      return null;
    }
  }

  /** Record (or clear) the endpoint the backend has been told about. */
  private setPostedEndpoint(endpoint: string | null): void {
    this.postedEndpoint = endpoint;
    if (endpoint === null) {
      removeStorage('local', PUSH_POSTED_ENDPOINT_STORAGE_KEY);
      return;
    }
    // A refused write costs one redundant re-POST on the next session (register is an idempotent
    // upsert on the endpoint), so it is not worth reporting.
    writeStorage('local', PUSH_POSTED_ENDPOINT_STORAGE_KEY, endpoint);
  }

  private pause(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private publish(status: PushStatus): PushStatus {
    this.statusSubject.next(status);
    return status;
  }
}

/**
 * Whether `subscription` was created with `key`.
 *
 * A browser that does not report the key it used (no `options`, or a null `applicationServerKey` --
 * older Safari) answers `true`: "unknown" must not read as "different", or every call would drop a
 * perfectly good subscription and mint a fresh endpoint.
 */
function createdWithKey(subscription: PushSubscription, key: Uint8Array): boolean {
  const current = subscription.options ? subscription.options.applicationServerKey : null;
  if (!current) {
    return true;
  }
  const bytes = new Uint8Array(current);
  if (bytes.length !== key.length) {
    return false;
  }
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] !== key[i]) {
      return false;
    }
  }
  return true;
}

/** The subscribe call wants raw bytes, not the base64url string. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}
